import { useState } from 'react';
import clsx from 'clsx';
import type { RetroSnapshot } from '@retro/shared';
import { useRetroStore } from '../../lib/retroStore.js';
import { Avatar, Modal } from '../ui.js';

/**
 * The participant list. When the viewer facilitates and voting has started, it
 * doubles as a progress panel showing how many votes each person has left —
 * never which cards they voted for. Facilitators can also remove a guest (for
 * instance one who has left), which takes them and their input out of the room.
 */
export function ParticipantList({ snapshot }: { snapshot: RetroSnapshot }) {
  const send = useRetroStore((state) => state.send);
  const [toRemove, setToRemove] = useState<{ id: string; name: string } | null>(null);

  const facilitators = snapshot.participants.filter((p) => p.role === 'FACILITATOR');
  const contributors = snapshot.participants.filter((p) => p.role !== 'FACILITATOR');

  const groups = [
    { label: 'Facilitators', people: facilitators },
    { label: 'Participants', people: contributors },
  ].filter((group) => group.people.length > 0);

  const canRemoveGuests = snapshot.me.isFacilitator && snapshot.retro.phase !== 'CLOSED';

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.label}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink/45">{group.label}</p>
          <ul className="space-y-1.5">
            {group.people.map((person) => {
              const isMe = person.id === snapshot.me.participantId;
              return (
                <li key={person.id} className="group/participant flex items-center gap-2.5">
                  <Avatar
                    emoji={person.avatar}
                    name={person.name}
                    size="sm"
                    online={person.isOnline}
                    ready={person.isReady || undefined}
                  />
                  <span className="min-w-0 flex-1">
                    <span className={clsx('block truncate text-sm', !person.isOnline && 'text-ink/45')}>
                      {person.name}
                      {isMe ? ' (you)' : ''}
                    </span>
                    {person.kind === 'GUEST' ? (
                      <span className="block text-[11px] text-ink/45">Guest</span>
                    ) : null}
                  </span>

                  {person.votesRemaining !== undefined ? (
                    <span
                      className={clsx(
                        'chip shrink-0',
                        person.hasFinishedVoting ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800',
                      )}
                      title="Votes left"
                    >
                      {person.hasFinishedVoting ? 'done' : `${person.votesRemaining} left`}
                    </span>
                  ) : null}

                  {canRemoveGuests && person.kind === 'GUEST' && !isMe ? (
                    <button
                      type="button"
                      onClick={() => setToRemove({ id: person.id, name: person.name })}
                      aria-label={`Remove ${person.name}`}
                      title={`Remove ${person.name}`}
                      className="shrink-0 rounded-full p-1 text-ink/40 transition hover:bg-rose-50 hover:text-rose-600 focus-visible:opacity-100 sm:opacity-0 sm:group-hover/participant:opacity-100"
                    >
                      ✕
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      <Modal
        open={toRemove !== null}
        onClose={() => setToRemove(null)}
        width="sm"
        title="Remove this guest?"
        footer={
          <>
            <button type="button" className="btn" onClick={() => setToRemove(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary bg-rose-600 hover:bg-rose-700"
              onClick={() => {
                if (toRemove) void send('participant:remove', { participantId: toRemove.id });
                setToRemove(null);
              }}
            >
              Remove
            </button>
          </>
        }
      >
        <p className="text-sm text-ink/70">
          <span className="font-medium">{toRemove?.name}</span> will be removed from this
          retrospective, along with anything they have added. This cannot be undone.
        </p>
      </Modal>
    </div>
  );
}
